// The photos and files waiting in a chat box (TOM-671). Each is uploaded the
// moment it is added (§12.17), by the + menu, a paste or a drop, so a send
// carries only ids. The message goes once every upload has finished; one that
// failed is shown with why, and is removed by its ✕.
//
// An upload the owner removes before it lands is dropped on arrival. The
// server deletes an unsent attachment after 24 hours, so nothing is cleaned up
// here.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  chatAttachmentKind,
  chatUploadErrorMessage,
  parseUploadedChatAttachment,
  pickChatAttachments,
  prepareChatPhoto,
  type ChatAttachmentKind,
  type UploadedChatAttachment,
} from './chatAttachments';

export interface PendingChatAttachment {
  /** This box's own key for it. */
  key: string;
  name: string;
  kind: ChatAttachmentKind;
  /** A local preview of a photo, until it is sent. */
  previewUrl: string | null;
  status: 'uploading' | 'ready' | 'failed';
  /** Set once the server holds it. */
  uploaded: UploadedChatAttachment | null;
  error: string | null;
}

export interface ChatAttachments {
  items: PendingChatAttachment[];
  /** Hand the box files from the picker, a paste or a drop. */
  add: (files: readonly File[]) => void;
  remove: (key: string) => void;
  /** Forget every file, once the message carrying them is sent. */
  clear: () => void;
  /** Put sent files back in the box, when the message did not go. */
  restore: (sent: readonly UploadedChatAttachment[]) => void;
  /** True while any file is still uploading. */
  uploading: boolean;
  /** The uploaded files, in the order added. */
  ready: UploadedChatAttachment[];
  /** The latest refusal, for the line under the box. */
  notice: string | null;
  dismissNotice: () => void;
}

let nextKey = 0;

async function upload(file: File): Promise<UploadedChatAttachment> {
  const kind = chatAttachmentKind(file);
  const body = kind === 'image' ? await prepareChatPhoto(file) : file;
  const form = new FormData();
  form.append('file', body, body.name);
  const res = await fetch('/v1/neo/attachments', {
    method: 'POST',
    body: form,
    credentials: 'same-origin',
  });
  const json = (await res.json().catch(() => null)) as { message?: unknown } | null;
  if (!res.ok) throw new Error(chatUploadErrorMessage(res.status, json, file.name));
  const parsed = parseUploadedChatAttachment(json);
  if (parsed === null) throw new Error(`${file.name} didn’t upload. Try again.`);
  return parsed;
}

export function useChatAttachments(): ChatAttachments {
  const [items, setItems] = useState<PendingChatAttachment[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Local previews are freed when their file leaves the box, and on unmount.
  const revoke = (item: PendingChatAttachment) => {
    if (item.previewUrl !== null && item.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(item.previewUrl);
    }
  };
  useEffect(() => () => itemsRef.current.forEach(revoke), []);

  const add = useCallback((files: readonly File[]) => {
    const pick = pickChatAttachments(files, itemsRef.current.length);
    setNotice(pick.refused.length > 0 ? pick.refused.join(' ') : null);
    if (pick.accepted.length === 0) return;
    const added = pick.accepted.map((file): PendingChatAttachment & { file: File } => {
      const kind = chatAttachmentKind(file) ?? 'file';
      nextKey += 1;
      return {
        key: `att-${nextKey}`,
        name: file.name.length > 0 ? file.name : kind === 'image' ? 'Photo' : 'File',
        kind,
        previewUrl: kind === 'image' ? URL.createObjectURL(file) : null,
        status: 'uploading',
        uploaded: null,
        error: null,
        file,
      };
    });
    setItems((prev) => [...prev, ...added.map(({ file: _file, ...item }) => item)]);
    for (const { key, file } of added) {
      void upload(file).then(
        (uploaded) =>
          setItems((prev) =>
            prev.map((i) => (i.key === key ? { ...i, status: 'ready', uploaded } : i))
          ),
        (err: unknown) =>
          setItems((prev) =>
            prev.map((i) =>
              i.key === key
                ? {
                    ...i,
                    status: 'failed',
                    error: err instanceof Error ? err.message : `${file.name} didn’t upload.`,
                  }
                : i
            )
          )
      );
    }
  }, []);

  const remove = useCallback((key: string) => {
    setItems((prev) => {
      const gone = prev.find((i) => i.key === key);
      if (gone !== undefined) revoke(gone);
      return prev.filter((i) => i.key !== key);
    });
  }, []);

  const clear = useCallback(() => {
    setItems((prev) => {
      prev.forEach(revoke);
      return [];
    });
    setNotice(null);
  }, []);

  const restore = useCallback((sent: readonly UploadedChatAttachment[]) => {
    if (sent.length === 0) return;
    setItems((prev) => [
      ...sent.map((uploaded): PendingChatAttachment => {
        nextKey += 1;
        return {
          key: `att-${nextKey}`,
          name: uploaded.filename ?? (uploaded.kind === 'image' ? 'Photo' : 'File'),
          kind: uploaded.kind,
          previewUrl: null,
          status: 'ready',
          uploaded,
          error: null,
        };
      }),
      ...prev,
    ]);
  }, []);

  const ready = items.flatMap((i) => (i.uploaded === null ? [] : [i.uploaded]));
  return {
    items,
    add,
    remove,
    clear,
    restore,
    uploading: items.some((i) => i.status === 'uploading'),
    ready,
    notice,
    dismissNotice: useCallback(() => setNotice(null), []),
  };
}
